import { Observable, Subscription, firstValueFrom } from 'rxjs';
import type { StepParams } from '@enlighten/ts-shared/step-api';
import { API_BASE } from '../../../constants/general.constants';
import { Secret } from '../../../interfaces/secrets.interface';
import { LlmAvailableModel } from '../../../interfaces/llm.interface';
import { ExecutionContext } from '@app/shared/interfaces/chunk/chunk-context.interface';
import {
  getSecretValueViaBridge,
  sendNotificationViaBridge,
} from './script-notification.helper';

/** Minimal shape of the connection records read from ConnectionService. */
interface ConnectionLike {
  name: string;
  type: string;
  description?: string | null;
  config?: { provider?: string | null; bucket?: string | null; prefix?: string | null };
}

/** Minimal shape of the step records read from StepService.stepList. */
interface StepLike {
  stepId: number;
  name: string;
  steptypeId: number;
  disabled: boolean;
}

/** Connection metadata exposed to step code (EHQ.listConnections / getConnection). */
interface ConnectionInfo {
  name: string;
  type: string;
  description: string | null;
}

/** Object-storage connection metadata (EHQ.listObjectStorage). */
interface ObjectStorageInfo extends ConnectionInfo {
  provider: string | null;
  bucket: string | null;
  prefix: string | null;
}

/** Step metadata exposed to step code (EHQ.listSteps / getStepInfo). */
interface StepSummary {
  stepId: number;
  name: string;
  position: number;
  stepType: string;
  disabled: boolean;
}

interface WorkflowInfo { workflowId: number; name: string | null; }
interface ProjectInfo { projectId: number; name: string | null; description: string | null; }
interface DeploymentInfo { deploymentId: string; }

/** The slice of LlmService the bridge needs (EHQ.listLLMModels). */
interface LlmModelLister {
  listModelsOrThrow(projectId: number): Observable<LlmAvailableModel[]>;
}

/**
 * A readable message for a failed HTTP call. Handles an HttpErrorResponse and
 * the plain object the error interceptor rethrows for ArrayBuffer bodies.
 */
function describeHttpError(error: unknown): string {
  if (error && typeof error === 'object') {
    const e = error as { status?: unknown; error?: unknown; message?: unknown };
    const body = e.error;
    const bodyMessage =
      typeof body === 'string' && body.trim()
        ? body.trim()
        : body && typeof body === 'object' && typeof (body as { message?: unknown }).message === 'string'
          ? (body as { message: string }).message
          : undefined;
    const detail = bodyMessage ?? (typeof e.message === 'string' ? e.message : undefined);
    if (typeof e.status === 'number') {
      const status = e.status === 0 ? 'network error' : `HTTP ${e.status}`;
      return detail ? `${status}: ${detail}` : status;
    }
    if (detail) return detail;
  }
  return String(error);
}

/** LLM model metadata (EHQ.listLLMModels); same keys enoch returns. */
interface LlmModelInfo {
  modelName: string;
  displayName: string;
  llmType: string;
  isDefault: boolean;
}

/** Secret metadata (never the value) exposed to step code. */
type SecretMetadata = Pick<Secret, 'secretId' | 'name' | 'gettable' | 'sourceId' | 'sourceType' | 'sourceName'>;

/**
 * IntrospectionBridgeService — adapts Angular services for the EHQ introspection API.
 *
 * Metadata methods are synchronous to match enoch's behavior — the same Python
 * code must work identically in both browser and headless runtimes. The one
 * exception is getSecretValue (SCRUM-584), which is async because it performs an
 * audited server round-trip and is registered through the async import-bridge
 * pathway.
 *
 * Not an Angular injectable — instantiated manually in PythonService.
 */
export class IntrospectionBridgeService {
  private stepTypeCache: Map<number, string> = new Map();
  private stepTypesSubscription?: Subscription;

  // Cached project secrets METADATA (never values) for the sync listSecrets/
  // getSecret API. Populated at construction from the secrets list endpoint,
  // which returns the sanitized shape (no value). getSecretValue does NOT use
  // this cache — it always performs a fresh server round-trip + audit.
  private secretsCache: Secret[] = [];

  // The injected Angular services are kept `any` in this pass: tightening them to
  // their concrete classes needs the caller (PythonService) verified against each
  // member access and a full typecheck, so it is deferred to a focused change.
  constructor(
    private stepService: any,
    private stepTypeService: any,
    private connectionService: any,
    private appStateService: any,
    private oauthService?: any,
    private secretsService?: any,
    private pushDestinationService?: any,
    private deploymentInfoService?: any,
    private llmService?: LlmModelLister,
  ) {
    this.refreshStepTypeCache();
    this.refreshSecretsCache();
  }

  /**
   * Unsubscribe from step types; call before replacing the instance (e.g. Python init retry).
   */
  dispose(): void {
    this.stepTypesSubscription?.unsubscribe();
    this.stepTypesSubscription = undefined;
  }

  /**
   * Fetch the project's secrets metadata into the sync cache.
   *
   * The list endpoint returns the sanitized shape (no value), so caching it is
   * safe — only metadata ever lives in memory. Fired once at construction; the
   * bridge is reconstructed per project context (see PythonService.initializeEHQBridge),
   * so the cache always reflects the active project.
   */
  private refreshSecretsCache(): void {
    const projectId = this.appStateService.projectId?.() ?? this.appStateService.project?.()?.projectId;
    if (!projectId || !this.secretsService?.getSecrets) {
      return;
    }
    this.secretsService.getSecrets(projectId).subscribe({
      next: (secrets: Secret[]) => {
        // Defense-in-depth: cache metadata only, never values. Even though the
        // list endpoint returns the sanitized shape, strip `value` explicitly so
        // a value can never linger in memory if the backend regresses or a
        // different endpoint is wired in here by mistake.
        this.secretsCache = (secrets ?? []).map(
          s =>
            ({
              secretId: s.secretId,
              name: s.name,
              gettable: s.gettable,
              sourceId: s.sourceId,
              sourceType: s.sourceType,
              sourceName: s.sourceName,
            }) as Secret,
        );
      },
      error: (err: unknown) => {
        console.warn('[IntrospectionBridge] failed to load secrets metadata:', err);
        this.secretsCache = [];
      },
    });
  }

  private refreshStepTypeCache(): void {
    this.stepTypesSubscription?.unsubscribe();
    const stream = this.stepTypeService.stepTypes$;
    if (!stream?.subscribe) {
      return;
    }
    this.stepTypesSubscription = stream.subscribe((types: Array<{ steptypeId: number; name: string }>) => {
      if (types) {
        this.stepTypeCache.clear();
        for (const t of types) {
          this.stepTypeCache.set(t.steptypeId, t.name);
        }
      }
    });
  }

  private getStepTypeName(steptypeId: number): string {
    return this.stepTypeCache.get(steptypeId) || 'Unknown';
  }

  // The methods below take the params the shared parsers return (EHQ-10):
  // both bridges validate the call before it reaches the service.
  listConnections(params: StepParams['EHQ.listConnections']): ConnectionInfo[] {
    const typeFilter = params.type;
    // Read from the signal cache — already populated by the time
    // a user opens a project and runs a step.
    let connections = this.connectionService.connections();
    if (typeFilter) {
      connections = connections.filter((c: ConnectionLike) => c.type === typeFilter);
    }
    return connections.map((c: ConnectionLike) => ({
      name: c.name,
      type: c.type,
      description: c.description || null,
    }));
  }

  getConnection(params: StepParams['EHQ.getConnection']): ConnectionInfo | null {
    const name = params.name;
    const connections = this.connectionService.connections();
    const conn = connections.find((c: ConnectionLike) => c.name === name);
    if (!conn) return null;
    return {
      name: conn.name,
      type: conn.type,
      description: conn.description || null,
    };
  }

  listSteps(): StepSummary[] {
    const steps = this.stepService.stepList;
    return steps.map((s: StepLike, i: number) => ({
      stepId: s.stepId,
      name: s.name,
      position: i,
      stepType: this.getStepTypeName(s.steptypeId),
      disabled: s.disabled,
    }));
  }

  /**
   * Current step metadata. Both bridges call it as `(params, chunkContext)`.
   */
  // `chunkContext` stays `any`: the lookup probes `getChunk`, which lives on
  // ChunkDataContext rather than ExecutionContext.
  getStepInfo(_params: StepParams['EHQ.getStepInfo'], chunkContext?: any): StepSummary | null {
    const ctx = chunkContext;
    const steps = this.stepService.stepList;
    if (!steps?.length) {
      return null;
    }
    const currentStepId = ctx?.getChunk?.()?.stepId
      ?? this.stepService.selectedStepId;
    const idx = steps.findIndex((s: StepLike) => s.stepId === currentStepId);
    const step = idx >= 0 ? steps[idx] : steps[0];
    const position = idx >= 0 ? idx : 0;
    return {
      stepId: step.stepId,
      name: step.name,
      position,
      stepType: this.getStepTypeName(step.steptypeId),
      disabled: step.disabled,
    };
  }

  getJobInfo(): null {
    return null;
  }

  // ==== Phase 3: Additional introspection methods ====

  getWorkflowInfo(): WorkflowInfo {
    const workflow = this.appStateService.workflow();
    return {
      workflowId: workflow?.workflowId || 0,
      name: workflow?.name || null,
    };
  }

  getProjectInfo(): ProjectInfo {
    const project = this.appStateService.project();
    return {
      projectId: project?.projectId || 0,
      name: project?.name || null,
      description: project?.description || null,
    };
  }

  /**
   * Deployment identifier for the current runtime (SCRUM-782). Mirrors enoch's
   * EHQ.getDeploymentInfo() so step code reads the deployment id portably across
   * runtimes — getJobInfo() is null in the browser, but this still resolves.
   * Sourced from the server's deployment-info (DeploymentInfoService), so a local
   * browser pointed at dev reports `dev`, not `localhost`.
   */
  getDeploymentInfo(): DeploymentInfo {
    return {
      deploymentId: this.deploymentInfoService?.deploymentId() ?? 'unknown',
    };
  }

  isFeatureEnabled(params: StepParams['EHQ.isFeatureEnabled']): boolean {
    const featureName = params.name;

    const typeMap: Record<string, string> = {
      llm: 'LLM',
      datafusion: 'DataFusion',
      box: 'Box',
      objectstorage: 'ObjectStorage',
      database: 'Database',
    };
    const connType = typeMap[featureName.toLowerCase()];
    if (connType) {
      const connections = this.connectionService.connections();
      return connections.some((c: ConnectionLike) => c.type === connType);
    }
    return false;
  }

  /**
   * listLLMModels() — the LLM models available to the active project (EHQ-386).
   *
   * Mirrors enoch's EHQ.listLLMModels(): async, returns
   * `{ modelName, displayName, llmType, isDefault }` per model, with isDefault
   * defaulting to false. Backed by LlmService.listModelsOrThrow, which reads
   * GET /project/{id}/llm/models. Like enoch, an HTTP failure throws rather
   * than returning an empty list. Registered through the async import-bridge
   * pathway (CUSTOM_METHODS_IMPORT) for both JS and Python, which turns the
   * rejection into an ordinary thrown error in step code.
   */
  async listLLMModels(): Promise<LlmModelInfo[]> {
    const projectId = this.appStateService.projectId?.() ?? this.appStateService.project?.()?.projectId;
    if (!projectId) {
      throw new Error('listLLMModels: no active project');
    }
    if (!this.llmService) {
      throw new Error('listLLMModels: LLM service unavailable');
    }
    let models: LlmAvailableModel[];
    try {
      models = await firstValueFrom(this.llmService.listModelsOrThrow(projectId));
    } catch (error) {
      throw new Error(`listLLMModels: could not list LLM models (${describeHttpError(error)})`);
    }
    return (models ?? []).map(m => ({
      modelName: m.modelName,
      displayName: m.displayName,
      llmType: m.llmType,
      isDefault: m.isDefault ?? false,
    }));
  }

  listObjectStorage(): ObjectStorageInfo[] {
    const connections = this.connectionService.connections();
    return connections
      .filter((c: ConnectionLike) => c.type === 'ObjectStorage')
      .map((c: ConnectionLike) => ({
        name: c.name,
        type: c.type,
        description: c.description || null,
        provider: c.config?.provider || null,
        bucket: c.config?.bucket || null,
        prefix: c.config?.prefix || null,
      }));
  }

  // ==== Phase 2: Control flow ====

  /**
   * skipToStep — browser implementation.
   *
   * In the browser, step execution is single-step (user clicks Run on one step).
   * skipToStep logs the intent but does not alter execution — there is no
   * multi-step execution loop to skip within. The step completes normally.
   *
   * Full skip behavior is headless-only (enoch).
   */
  skipToStep(params: StepParams['EHQ.skipToStep'], chunkContext: ExecutionContext): void {
    const targetName = params.stepName;
    const steps = this.stepService.stepList;
    const target = steps.find((s: StepLike) => s.name === targetName);
    if (!target) {
      throw new Error(`skipToStep: step "${targetName}" not found`);
    }

    chunkContext?.addLog?.(
      `skipToStep("${targetName}") — noted (browser executes steps individually)`,
      'info',
    );
  }

  // ── Enoch compatibility (SCRUM-254) ─────────────────────────────────

  /**
   * getAuthToken — return the current OAuth bearer token.
   */
  getAuthToken(): string {
    return this.oauthService?.getAccessToken?.() || '';
  }

  /**
   * getApiUrl — return the base API URL from environment config.
   *
   * Matches enoch's EHQ.getApiUrl() contract exactly: the URL is absolute and
   * returned with a single trailing slash (e.g. "http://nc-server/api/") so step
   * code can build endpoints by direct concatenation — `f"{api_url}fetch-proxy"`
   * — identically in the browser and in headless execution.
   *
   * API_BASE may be relative (e.g. "/api" in local/runtime-configured builds) or
   * already absolute (dev/stage/local-server). A relative base is resolved against
   * the page origin so the result is always absolute; enoch returns an absolute
   * URL and ehq.fetch() rejects non-http(s) URLs, so a relative value broke any
   * browser-run step that proxied a fetch via getApiUrl() (SCRUM-742). API_BASE is
   * stored without a trailing slash (see environment.*.ts), so we append one.
   */
  getApiUrl(): string {
    const base = API_BASE || '';
    if (!base) {
      return '';
    }
    let absolute = base;
    if (!/^https?:\/\//i.test(base)) {
      const path = base.startsWith('/') ? base : `/${base}`;
      absolute = `${window.location.origin}${path}`;
    }
    return absolute.endsWith('/') ? absolute : `${absolute}/`;
  }

  // ── Secrets (SCRUM-584) ─────────────────────────────────────────────
  //
  // Metadata (listSecrets/getSecret) is SYNC, served from the in-memory
  // secretsCache — values are never present in that cache. The value
  // (getSecretValue) is ASYNC: it always hits the server, which performs
  // access checks and audit logging. API contract matches enoch exactly.

  /**
   * Map a cached Secret to the metadata shape exposed to step code.
   * Strictly excludes the value.
   */
  private toSecretMetadata(s: Secret): SecretMetadata {
    return {
      secretId: s.secretId,
      name: s.name,
      gettable: s.gettable,
      sourceId: s.sourceId,
      sourceType: s.sourceType,
      sourceName: s.sourceName,
    };
  }

  /**
   * listSecrets() — return metadata for all project secrets (no values).
   */
  listSecrets(): SecretMetadata[] {
    return this.secretsCache.map(s => this.toSecretMetadata(s));
  }

  /**
   * getSecret(name) — return metadata for one secret by name, or null.
   */
  getSecret(params: StepParams['EHQ.getSecret']): SecretMetadata | null {
    const name = params.name;
    const secret = this.secretsCache.find(s => s.name === name);
    if (!secret) return null;
    return this.toSecretMetadata(secret);
  }

  /**
   * getSecretValue(nameOrId) — resolve a secret value via the server.
   *
   * Accepts EITHER a secret name (string → resolved to id via the metadata
   * cache) OR a secret id (number → used directly). Always performs a server
   * round-trip so the server can enforce access control and write an audit
   * record. Async: invoked through the import (async) bridge pathway.
   *
   * Signature matches the import-bridge contract: (params, chunkContext, mode).
   */
  async getSecretValue(params: StepParams['EHQ.getSecretValue']): Promise<string> {
    return getSecretValueViaBridge(
      params,
      this.appStateService,
      this.secretsService,
      name => this.secretsCache.find(s => s.name === name),
    );
  }

  /**
   * sendNotification(destination, message, options?) — send a script-triggered
   * ("Manual") notification to a configured push destination. Forwards to the
   * server's POST /push-destinations/send; delivery is asynchronous and
   * rate-limited server-side. Async: invoked through the import bridge pathway.
   */
  async sendNotification(params: StepParams['EHQ.sendNotification']): Promise<boolean> {
    return sendNotificationViaBridge(
      params,
      this.appStateService,
      this.pushDestinationService,
    );
  }
}
